import type { FlowDefinition, FlowFieldDefinition, FlowNode } from '../types';
import { VASE_FLOW_FIELD_IDS, rebuildFlowConnections } from './flows';
import { resolveOrCreateSectionInsertionFlow } from './sectionInsertionFlow';
import { isProductGalleryContent } from './productGallery';

const PRODUCT_GALLERY_FLOW_FIELDS: FlowFieldDefinition[] = [
  {
    id: VASE_FLOW_FIELD_IDS.chosenProduct,
    label: 'Chosen Product',
    type: 'entity.product',
  },
];

function normalizeGalleryLabel(label: string | null | undefined) {
  const trimmed = typeof label === 'string' ? label.trim() : '';
  return trimmed.length > 0 ? trimmed : 'Product Gallery';
}

export function getProductGallerySelectFieldId(blockId: string) {
  return `gallery-select-${blockId}`;
}

export function getProductGallerySelectionFieldId(blockId: string) {
  return `gallery-selection-${blockId}`;
}

function buildProductGallerySelectField(blockId: string, label: string | null | undefined): FlowFieldDefinition {
  return {
    id: getProductGallerySelectFieldId(blockId),
    label: `${normalizeGalleryLabel(label)} Selected`,
    type: 'event.trigger',
  };
}

function buildProductGallerySelectionField(blockId: string, label: string | null | undefined): FlowFieldDefinition {
  return {
    id: getProductGallerySelectionFieldId(blockId),
    label: `${normalizeGalleryLabel(label)} Selected Product`,
    type: 'context.selection',
  };
}

function upsertFields(fields: FlowFieldDefinition[], additions: FlowFieldDefinition[]) {
  const nextById = new Map(fields.map((field) => [field.id, field]));
  additions.forEach((field) => {
    if (!nextById.has(field.id)) {
      nextById.set(field.id, field);
    }
  });
  return Array.from(nextById.values());
}

function upsertNodes(nodes: FlowNode[], additions: FlowNode[]) {
  const nextByBlockId = new Map(nodes.map((node) => [node.blockId, node]));
  additions.forEach((node) => {
    const existing = nextByBlockId.get(node.blockId);
    if (!existing) {
      nextByBlockId.set(node.blockId, node);
      return;
    }

    const mergeBindings = (
      current: FlowNode['inputs'] | FlowNode['outputs'],
      defaults: FlowNode['inputs'] | FlowNode['outputs']
    ) => {
      const next = [...current];
      defaults.forEach((binding) => {
        if (!next.some((entry) => entry.socketId === binding.socketId)) {
          next.push(binding);
        }
      });
      return next;
    };

    const replaceOutputBindings = (
      current: FlowNode['outputs'],
      defaults: FlowNode['outputs']
    ) => {
      const defaultSocketIds = new Set(defaults.map((binding) => binding.socketId));
      return [
        ...current.filter((binding) => !defaultSocketIds.has(binding.socketId)),
        ...defaults,
      ];
    };

    nextByBlockId.set(node.blockId, {
      ...existing,
      id: existing.id || node.id,
      blockType: node.blockType,
      config: { ...(node.config ?? {}), ...(existing.config ?? {}) },
      inputs: mergeBindings(existing.inputs, node.inputs),
      outputs: replaceOutputBindings(existing.outputs, node.outputs),
    });
  });
  return Array.from(nextByBlockId.values());
}

export function upsertProductGalleryFlowNode(
  flows: FlowDefinition[],
  args: {
    sectionId: string;
    blockId: string;
    label?: string | null;
    sections?: Array<{
      id: string;
      settings?: {
        canvas?: {
          blocks?: Array<{
            id: string;
            type: string;
          }>;
        };
      };
    }> | null;
  }
) {
  const { flows: nextFlows, flow: targetFlow } = resolveOrCreateSectionInsertionFlow(flows, {
    sectionId: args.sectionId,
    sections: args.sections,
  });

  if (!targetFlow) return nextFlows;

  const nextNode: FlowNode = {
    id: `flow-node-${args.blockId}`,
    blockId: args.blockId,
    blockType: 'gallery',
    inputs: [],
    outputs: [
      { socketId: 'product', fieldId: getProductGallerySelectionFieldId(args.blockId) },
      { socketId: 'select', fieldId: getProductGallerySelectFieldId(args.blockId) },
    ],
  };

  const updatedFlowBase: FlowDefinition = {
    ...targetFlow,
    fields: upsertFields(targetFlow.fields, [
      ...PRODUCT_GALLERY_FLOW_FIELDS,
      buildProductGallerySelectionField(args.blockId, args.label),
      buildProductGallerySelectField(args.blockId, args.label),
    ]),
    nodes: upsertNodes(targetFlow.nodes, [nextNode]),
  };

  const updatedFlow: FlowDefinition = {
    ...updatedFlowBase,
    connections: rebuildFlowConnections(updatedFlowBase),
  };

  return nextFlows.map((flow) => (flow.id === updatedFlow.id ? updatedFlow : flow));
}

export function upsertProductGalleryFlowNodesForSections(
  flows: FlowDefinition[],
  sections: Array<{
    id: string;
    settings?: {
      canvas?: {
        blocks?: Array<{
          id: string;
          type: string;
          content?: Record<string, unknown>;
        }>;
      };
    };
  }>
) {
  return sections.reduce((nextFlows, section) => {
    const galleries = section.settings?.canvas?.blocks?.filter(
      (block) => block.type === 'gallery' && isProductGalleryContent(block.content)
    );
    if (!galleries || galleries.length === 0) return nextFlows;
    return galleries.reduce(
      (currentFlows, block) =>
        upsertProductGalleryFlowNode(currentFlows, {
          sectionId: section.id,
          blockId: block.id,
          label:
            typeof block.content?.title === 'string'
              ? block.content.title
              : 'Product Gallery',
          sections,
        }),
      nextFlows
    );
  }, flows);
}
